import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import {
  ClientType,
  PackageType,
  PartnershipType,
  ScopeType,
  useGetAllClientsQuery,
  useGetAllPackagesQuery,
  useGetPartnershipsQuery,
  useGetRevenueSharesQuery,
} from '../types';
import { groupRevenueShares, IRevenueShare } from '../utils/discount-utils';
import { cleanTypeNameGraphql } from '../utils/graphql-utils';

export interface GetRevenueSharesOptions {
  clientId?: number; // omit to fetch all (global view)
  partnershipIds?: number[];
  skip?: boolean;
}

export interface UseGetRevenueSharesResult {
  data: IRevenueShare[];
  isLoading: boolean;
  hasError: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGetRevenueShares = ({
  clientId,
  partnershipIds,
  skip,
}: GetRevenueSharesOptions): UseGetRevenueSharesResult => {
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipIds are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useGetRevenueSharesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      clientId,
      partnershipIds,
      scopeType: ScopeType.Inclusive,
    },
    skip,
  });

  const data = useMemo(
    () =>
      queryData?.getRevenueShares
        ? groupRevenueShares(queryData.getRevenueShares)
        : [],
    [queryData],
  );

  return {
    data,
    isLoading: loading,
    hasError: !!error,
    error,
    refetch,
  };
};

export interface UseRevenueSharePageDataResult {
  revenueShares: IRevenueShare[];
  revenueSharesLoading: boolean;
  clients: ClientType[];
  clientsLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  packages: PackageType[];
  packagesLoading: boolean;
  clientMap: Map<number, string>;
  partnershipMap: Map<number, string>;
}

export const useRevenueSharePageData = (): UseRevenueSharePageDataResult => {
  const { data: revenueShares, isLoading: revenueSharesLoading } =
    useGetRevenueShares({});

  const { data: clientsData, loading: clientsLoading } =
    useGetAllClientsQuery();
  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({ variables: { type: 'billing' } });
  const { data: packagesData, loading: packagesLoading } =
    useGetAllPackagesQuery();

  const { clients, clientMap } = useMemo(() => {
    const sorted = [...(clientsData?.getAllClients ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const map = new Map<number, string>();
    sorted.forEach(c => map.set(c.id, c.name));
    return { clients: sorted, clientMap: map };
  }, [clientsData]);

  const { partnerships, partnershipMap } = useMemo(() => {
    const sorted = [...(partnershipsData?.getPartnerships ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const map = new Map<number, string>();
    sorted.forEach(p => map.set(p.id, p.name));
    return { partnerships: sorted, partnershipMap: map };
  }, [partnershipsData]);

  const packages = useMemo(
    () =>
      [
        ...(packagesData
          ? cleanTypeNameGraphql(packagesData.getAllPackages)
          : []),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [packagesData],
  );

  return {
    revenueShares,
    revenueSharesLoading,
    clients,
    clientsLoading,
    partnerships,
    partnershipsLoading,
    packages,
    packagesLoading,
    clientMap,
    partnershipMap,
  };
};
